import React, { useState, useEffect, useRef } from 'react';
import { Referral, TriageData, ReferralComment, Attachment, isSrReferral } from '../services/apiService';
import { ApiService } from '../services/apiService';
import { PDFPreviewPanel, AttachmentLike } from './PDFPreviewPanel';
import { TabMenu } from './TabMenu';
import { TabMenuItem } from './TabMenuItem';
import { EditableField } from './EditableField';
import { Button } from './Button';
import { TagInput } from './TagInput';
import { ConfirmationModal } from './ConfirmationModal';
import { Spinner } from './Spinner';
import { ActionMenu } from './ActionMenu';
import { IngestionWarningsBanner } from './IngestionWarningsBanner';
import { SrChangeBanner } from './sr/SrChangeBanner';

import FeatherIcon from 'feather-icons-react';

interface TriageDetailModalProps {
  referral: Referral | null;
  isOpen: boolean;
  onClose: () => void;
  onReferralUpdate?: (updatedReferral: Referral) => void;
  userRole?: string;
}

export const TriageDetailModal: React.FC<TriageDetailModalProps> = ({
  referral,
  isOpen,
  onClose,
  onReferralUpdate,
  userRole: propUserRole,
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'attachments'>('overview');
  const [focusAttachmentId, setFocusAttachmentId] = useState<string | null>(null);

  // Triage state
  const [triageCategoryFinal, setTriageCategoryFinal] = useState<number | null>(null);
  const [savingTriage, setSavingTriage] = useState(false);
  const [approvingTriage, setApprovingTriage] = useState(false);
  const [triageError, setTriageError] = useState<string | null>(null);
  const [editingTriageField, setEditingTriageField] = useState<string | null>(null);
  const [editingTriageData, setEditingTriageData] = useState<Partial<TriageData>>({});
  const [downloadingAttachments, setDownloadingAttachments] = useState<Set<string>>(new Set());
  const [currentReferral, setCurrentReferral] = useState<Referral | null>(null);
  const [complexityValue, setComplexityValue] = useState<'acute' | 'simple' | 'complex' | null>(null);

  // AXIS165: Editable referral type (separate from triage_data since it's on referrals table)
  const [referralTypeValue, setReferralTypeValue] = useState<string | null>(null);
  const [clinicalNotesValue, setClinicalNotesValue] = useState<string>('');

  // AXIS165: Editable clinical fields state
  const [editableTriageFields, setEditableTriageFields] = useState<{
    key_phrases_matched: string[];
    primary_symptoms: string[];
    functional_limitations: string[];
    escalation_required: boolean | null;
    escalation_reason: string | null;
  }>({
    key_phrases_matched: [],
    primary_symptoms: [],
    functional_limitations: [],
    escalation_required: null,
    escalation_reason: null
  });

  // AXIS165: Track which fields have been overridden (edited)
  const [overriddenFields, setOverriddenFields] = useState<Set<string>>(new Set());

  // AXIS166: Comments state
  const [comments, setComments] = useState<ReferralComment[]>([]);
  const [newCommentBody, setNewCommentBody] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentsError, setCommentsError] = useState<string | null>(null);
  
  // AXIS207: Edit comment state
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentBody, setEditCommentBody] = useState('');
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(null);

  // AXIS207: Use userRole from props (passed from parent page)
  const userRole = propUserRole || null;

  // AXIS212: Upload state (for when no attachments exist)
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // AXISREF244: Online form attachment fetch warnings
  const [dismissedIngestionWarnings, setDismissedIngestionWarnings] = useState(false);

  // AXIS212: Reprocess state (highlighted after upload)
  const [showReprocessAfterUpload, setShowReprocessAfterUpload] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);

  // AXIS212: Reprocess confirmation modal state
  const [showReprocessConfirm, setShowReprocessConfirm] = useState(false);
  const [hasOverriddenFields, setHasOverriddenFields] = useState(false);
  const [isTriageApproved, setIsTriageApproved] = useState(false);

  // AXIS241: Delete attachment state
  const [deletingAttachments, setDeletingAttachments] = useState<Set<string>>(new Set());
  const [attachmentToDelete, setAttachmentToDelete] = useState<AttachmentLike | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // AXIS209: Approve for Triage state (for admin to return referral to triaged tab)
  const [approvingForTriage, setApprovingForTriage] = useState(false);

  // AXIS209: Assign to Admin state (for modal actions dropdown)
  const [assigningToAdmin, setAssigningToAdmin] = useState(false);

  // AXIS166: Load comments for the current referral
  const loadComments = async (referralId: string) => {
    try {
      setLoadingComments(true);
      setCommentsError(null);
      const commentsData = await ApiService.getReferralComments(referralId);
      setComments(commentsData);
    } catch (err) {
      console.error('Failed to load comments:', err);
      setCommentsError(err instanceof Error ? err.message : 'Failed to load comments');
    } finally {
      setLoadingComments(false);
    }
  };

  // AXIS212: Handle attachment uploaded - add to list and enable reprocess
  const handleAttachmentUploaded = (newAttachment: AttachmentLike) => {
    if (currentReferral) {
      // Create proper Attachment object from AttachmentLike
      const attachment: Attachment = {
        id: newAttachment.id,
        filename: newAttachment.filename,
        type: newAttachment.type || newAttachment.filename.split('.').pop()?.toLowerCase() || 'unknown',
        size: newAttachment.size,
        mime_type: newAttachment.mime_type || 'application/octet-stream',
        storage_key: newAttachment.storage_key || '',
        uploadedAt: newAttachment.uploadedAt || new Date().toISOString(),
        downloadUrl: '' // Will be fetched when needed
      };

      // Add new attachment to current referral
      const updatedAttachments = [...(currentReferral.attachments || []), attachment];
      const updatedReferral = { ...currentReferral, attachments: updatedAttachments };
      setCurrentReferral(updatedReferral);

      // Enable reprocess button highlight
      setShowReprocessAfterUpload(true);

      // Notify parent
      onReferralUpdate?.(updatedReferral);
    }
  };

  // AXIS212: Standalone upload handlers for when no attachments exist
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !currentReferral) return;

    // Reset state
    setUploadError(null);
    setUploadProgress(0);

    // Validate file type - PDF, DOCX, DOC allowed (AXIS223: DOCX/DOC auto-converted to PDF)
    const fileExt = '.' + file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['.pdf', '.docx', '.doc'];
    if (!allowedExtensions.includes(fileExt)) {
      console.warn(`[AXIS223] File upload rejected: unsupported file type - ${file.name}`);
      setUploadError('Only PDF, DOCX, and DOC files are allowed');
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError(`File too large. Maximum size: ${maxSize / 1024 / 1024}MB`);
      return;
    }

    // Start upload
    setUploadingAttachment(true);
    try {
      const result = await ApiService.uploadAttachment(
        currentReferral.id,
        file,
        (progress) => setUploadProgress(progress)
      );

      // Create attachment object from result
      const newAttachment: AttachmentLike = {
        id: result.attachment_id,
        filename: result.filename,
        type: 'pdf',
        size: result.file_size,
        mime_type: result.content_type,
        storage_key: result.blob_path,
        uploadedAt: result.uploaded_at
      };

      // Add to referral
      handleAttachmentUploaded(newAttachment);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (err) {
      console.error('Upload failed:', err);
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploadingAttachment(false);
      setUploadProgress(0);
    }
  };

  // AXIS212: Handle reprocess triage (called after upload or from action menu)
  const handleReprocessTriage = async () => {
    if (!currentReferral) return;

    // Check if triage has overridden fields
    const hasOverrides = overriddenFields.size > 0;
    setHasOverriddenFields(hasOverrides);

    // Check if triage is approved
    const isApproved = currentReferral.triage_approved || false;
    setIsTriageApproved(isApproved);

    // Show confirmation modal if there are overrides or if approved
    if (hasOverrides || isApproved) {
      setShowReprocessConfirm(true);
      return;
    }

    // Otherwise proceed directly
    await executeReprocess();
  };

  // AXIS212: Execute reprocess after confirmation
  const executeReprocess = async () => {
    if (!currentReferral) return;

    setReprocessing(true);
    setTriageError(null);

    try {
      const result = await ApiService.extractTriage(currentReferral.id);

      if (result.success) {
        // Fetch full referral to get updated triage data
        const updatedReferral = await ApiService.getReferralById(currentReferral.id);

        // AXISREF400: Merge OCR skip warnings returned by extract-triage immediately
        // (get_referral also persists them, but this avoids a second network round-trip).
        const ocrWarnings = result.attachment_ocr_warnings ?? [];
        if (ocrWarnings.length > 0) {
          const existing = updatedReferral.ingestion_warnings ?? [];
          const merged = [
            ...existing,
            ...ocrWarnings.filter((w) => !existing.includes(w)),
          ];
          updatedReferral.ingestion_warnings = merged;
        }

        setCurrentReferral(updatedReferral);

        // Reset reprocess highlight
        setShowReprocessAfterUpload(false);

        // Update overridden fields from new data
        if (updatedReferral.overridden_triage_fields) {
          setOverriddenFields(new Set(updatedReferral.overridden_triage_fields));
        } else {
          setOverriddenFields(new Set());
        }

        // Notify parent
        onReferralUpdate?.(updatedReferral);
      } else {
        setTriageError(result.message || 'Reprocess failed');
      }
    } catch (err) {
      console.error('Error reprocessing triage:', err);
      setTriageError(err instanceof Error ? err.message : 'Failed to reprocess triage');
    } finally {
      setReprocessing(false);
      setShowReprocessConfirm(false);
    }
  };

  // Only update from props when the modal opens/closes or referral ID changes
  useEffect(() => {
    if (isOpen && referral) {
      // Check if this is a different referral (by ID) or modal just opened
      const isNewReferral = !currentReferral || currentReferral.id !== referral.id;
      
      if (isNewReferral) {
        // Modal just opened with a new referral - load the referral data and reset state
        setCurrentReferral(referral);
        setTriageCategoryFinal(referral.triage_category_final || null);
        setTriageError(null);
        setEditingTriageField(null);
        setEditingTriageData({});
        setComplexityValue(referral.triage_data?.complexity || null);

        // AXIS165: Initialize referral type from referral data
        setReferralTypeValue(referral.referral_type || null);
        setClinicalNotesValue(referral.clinical_notes || '');

        // AXIS165: Initialize editable triage fields from triage_data
        if (referral.triage_data) {
          setEditableTriageFields({
            key_phrases_matched: referral.triage_data.key_phrases_matched || [],
            primary_symptoms: referral.triage_data.primary_symptoms || [],
            functional_limitations: referral.triage_data.functional_limitations || [],
            escalation_required: referral.triage_data.escalation_required ?? null,
            escalation_reason: referral.triage_data.escalation_reason || null
          });
        }

        // AXIS165: Load overridden fields from referral data
        if (referral.overridden_triage_fields) {
          setOverriddenFields(new Set(referral.overridden_triage_fields));
        } else {
          setOverriddenFields(new Set());
        }

        // AXIS166: Load comments for this referral
        loadComments(referral.id);
        setDismissedIngestionWarnings(false);
        setFocusAttachmentId(null);
      } else {
        // Same referral, just update the data without resetting editing state
        // Check if the referral actually changed (check by comparing key fields and attachments)
        const referralChanged = 
          referral.triage_approved !== currentReferral?.triage_approved ||
          referral.triage_category_final !== currentReferral?.triage_category_final ||
          referral.triage_approved_at !== currentReferral?.triage_approved_at;
        
        // Check if attachments have changed (new attachments added or count changed)
        const attachmentsChanged = 
          (referral.attachments?.length || 0) !== (currentReferral?.attachments?.length || 0) ||
          (referral.attachments && referral.attachments.length > 0 && 
           (!currentReferral?.attachments || currentReferral.attachments.length === 0));

        const ingestionWarningsChanged =
          JSON.stringify(referral.ingestion_warnings || []) !==
          JSON.stringify(currentReferral?.ingestion_warnings || []);
        
        if (referralChanged || attachmentsChanged || ingestionWarningsChanged) {
          // Always use the new referral's attachments if they exist, otherwise preserve existing ones
          // AXIS226: Preserve assigned_to_admin to prevent unwanted state changes from parent re-renders
          const updatedReferral = {
            ...referral,
            attachments: referral.attachments && referral.attachments.length > 0
              ? referral.attachments
              : currentReferral?.attachments || [],
            assigned_to_admin: currentReferral?.assigned_to_admin ?? referral.assigned_to_admin
          };
          setCurrentReferral(updatedReferral);
          // Update triage category if it changed
          if (referral.triage_category_final !== currentReferral?.triage_category_final) {
            setTriageCategoryFinal(referral.triage_category_final || null);
          }
          // Update complexity if it changed
          if (referral.triage_data?.complexity !== currentReferral?.triage_data?.complexity) {
            setComplexityValue(referral.triage_data?.complexity || null);
          }
          // AXIS165: Update referral type if it changed
          if (referral.referral_type !== currentReferral?.referral_type) {
            setReferralTypeValue(referral.referral_type || null);
          }
        }

        // AXIS165: Update editable fields if triage_data arrived (background fetch completed)
        // Only update if we haven't made any edits yet (editingTriageData is empty)
        const hasNotStartedEditing = Object.keys(editingTriageData).length === 0;
        if (hasNotStartedEditing && referral.triage_data) {
          const currentKeyPhrases = JSON.stringify(editableTriageFields.key_phrases_matched);
          const newKeyPhrases = JSON.stringify(referral.triage_data.key_phrases_matched || []);
          const currentSymptoms = JSON.stringify(editableTriageFields.primary_symptoms);
          const newSymptoms = JSON.stringify(referral.triage_data.primary_symptoms || []);
          const currentLimitations = JSON.stringify(editableTriageFields.functional_limitations);
          const newLimitations = JSON.stringify(referral.triage_data.functional_limitations || []);

          // Only update if the data has changed from what we currently have
          if (currentKeyPhrases !== newKeyPhrases ||
              currentSymptoms !== newSymptoms ||
              currentLimitations !== newLimitations) {
            setEditableTriageFields({
              key_phrases_matched: referral.triage_data.key_phrases_matched || [],
              primary_symptoms: referral.triage_data.primary_symptoms || [],
              functional_limitations: referral.triage_data.functional_limitations || [],
              escalation_required: referral.triage_data.escalation_required ?? null,
              escalation_reason: referral.triage_data.escalation_reason || null
            });
          }
        }
      }
    } else if (!isOpen) {
      // Modal closed - clear everything
      setCurrentReferral(null);
      setTriageCategoryFinal(null);
      setTriageError(null);
      setEditingTriageField(null);
      setEditingTriageData({});
      // AXIS166: Clear comments state
      setComments([]);
      setNewCommentBody('');
      setCommentsError(null);
      setFocusAttachmentId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, referral?.id, referral?.attachments?.length]);

  if (!isOpen || !currentReferral) {
    return null;
  }

  const srReferral = isSrReferral(currentReferral);
  const isManualTriageShell =
    srReferral &&
    !!currentReferral.triage_data &&
    currentReferral.triage_data.triage_category_suggested == null &&
    currentReferral.triage_data.confidence_percentage == null;
  const canReprocessTriage =
    !srReferral || Boolean((clinicalNotesValue || currentReferral.clinical_notes || '').trim());

  const toCapitalCase = (text: string | null | undefined): string => {
    if (!text) return '';
    return text
      .split(/[\s_-]+/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // AXIS165: Helper to render "Edited" badge for overridden fields
  const renderEditedBadge = (fieldName: string) => {
    if (!overriddenFields.has(fieldName)) return null;
    return (
      <span
        style={{
          fontSize: '0.7rem',
          padding: '2px 6px',
          backgroundColor: '#E84B37',
          color: 'white',
          borderRadius: '4px',
          marginLeft: '0.5rem',
          fontWeight: 500
        }}
      >
        Edited
      </span>
    );
  };

  const formatDateAsDDMMYYYY = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    } catch {
      return 'N/A';
    }
  };

  // AXIS208: Format DOB as DD MMM YYYY (e.g., "12 Mar 2026")
  const formatDOB = (dob: string | null | undefined): string => {
    if (!dob) return 'N/A';
    const date = new Date(dob);
    if (isNaN(date.getTime())) return 'N/A';
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFileSize = (bytes?: number | null) => {
    if (bytes == null || bytes <= 0) return '—';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // AXIS207: Check if user has clinical role (can post comments post-approval)
  const isClinicalRole = ['admin', 'manager', 'clinician'].includes(userRole?.toLowerCase() || '');
  
  // Debug logging for role detection
  console.log('[AXIS207 Debug] userRole:', userRole, 'isClinicalRole:', isClinicalRole, 'triage_approved:', currentReferral?.triage_approved);

  // AXIS166: Submit a new comment
  const handleSubmitComment = async () => {
    if (!currentReferral || !newCommentBody.trim()) return;

    try {
      setSubmittingComment(true);
      setCommentsError(null);
      const newComment = await ApiService.createReferralComment(
        currentReferral.id,
        newCommentBody.trim()
      );
      // Append new comment to local list
      setComments(prev => [...prev, newComment]);
      // Clear the textarea
      setNewCommentBody('');
    } catch (err) {
      console.error('Failed to submit comment:', err);
      setCommentsError(err instanceof Error ? err.message : 'Failed to submit comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // AXIS207: Edit a comment
  const handleEditComment = async (commentId: string) => {
    if (!currentReferral || !editCommentBody.trim()) return;

    try {
      setSubmittingComment(true);
      setCommentsError(null);
      const updatedComment = await ApiService.updateReferralComment(
        currentReferral.id,
        commentId,
        editCommentBody.trim()
      );
      // Update comment in local list
      setComments(prev => prev.map(c => c.id === commentId ? updatedComment : c));
      // Clear edit state
      setEditingCommentId(null);
      setEditCommentBody('');
    } catch (err) {
      console.error('Failed to edit comment:', err);
      setCommentsError(err instanceof Error ? err.message : 'Failed to edit comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // AXIS207: Delete a comment
  const handleDeleteComment = async (commentId: string) => {
    if (!currentReferral) return;

    try {
      setDeletingCommentId(commentId);
      setCommentsError(null);
      await ApiService.deleteReferralComment(currentReferral.id, commentId);
      // Remove comment from local list
      setComments(prev => prev.filter(c => c.id !== commentId));
    } catch (err) {
      console.error('Failed to delete comment:', err);
      setCommentsError(err instanceof Error ? err.message : 'Failed to delete comment');
    } finally {
      setDeletingCommentId(null);
    }
  };

  // AXIS207: Start editing a comment
  const startEditComment = (comment: ReferralComment) => {
    setEditingCommentId(comment.id);
    setEditCommentBody(comment.body);
  };

  // AXIS207: Cancel editing
  const cancelEditComment = () => {
    setEditingCommentId(null);
    setEditCommentBody('');
  };

  const handleDownloadAttachment = async (attachment: any) => {
    if (!currentReferral) return;

    try {
      setDownloadingAttachments(prev => new Set(prev).add(attachment.id));
      if (isSrReferral(currentReferral)) {
        await ApiService.downloadSrAttachment(
          currentReferral.id,
          attachment.id,
          attachment.filename,
        );
      } else {
        await ApiService.downloadAttachment(attachment.id, attachment.filename);
      }
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download attachment. Please try again.');
    } finally {
      setDownloadingAttachments(prev => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  // AXIS241: Delete attachment handlers
  const handleDeleteClick = (attachment: AttachmentLike) => {
    setAttachmentToDelete(attachment);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!attachmentToDelete || !currentReferral) return;

    setDeletingAttachments(prev => new Set(prev).add(attachmentToDelete.id));
    setShowDeleteConfirm(false);

    try {
      await ApiService.deleteAttachment(currentReferral.id, attachmentToDelete.id);

      // Update local state to remove the deleted attachment
      const updatedAttachments = currentReferral.attachments?.filter(
        att => att.id !== attachmentToDelete.id
      ) || [];

      const updatedReferral = {
        ...currentReferral,
        attachments: updatedAttachments,
        attachment_count: updatedAttachments.length
      };

      setCurrentReferral(updatedReferral);
      onReferralUpdate?.(updatedReferral);

      console.log(`[AXIS241] Attachment deleted: ${attachmentToDelete.filename}`);
    } catch (err) {
      console.error('Failed to delete attachment:', err);
      setUploadError(err instanceof Error ? err.message : 'Failed to delete attachment');
    } finally {
      setDeletingAttachments(prev => {
        const next = new Set(prev);
        next.delete(attachmentToDelete.id);
        return next;
      });
      setAttachmentToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setAttachmentToDelete(null);
    setShowDeleteConfirm(false);
  };

  const handleStartEditingTriageField = (fieldName: string) => {
    if (!currentReferral?.triage_data) return;
    setEditingTriageField(fieldName);
    const currentValue = currentReferral.triage_data[fieldName as keyof TriageData];
    // Handle array fields - default to empty array if null/undefined
    if (fieldName === 'primary_symptoms' || fieldName === 'functional_limitations' || fieldName === 'comorbidities') {
      setEditingTriageData({
        [fieldName]: Array.isArray(currentValue) ? currentValue : []
      });
    } else {
      setEditingTriageData({
        [fieldName]: currentValue ?? null
      });
    }
  };

  const handleTriageFieldChange = (fieldName: string, value: any) => {
    setEditingTriageData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleCancelTriageFieldEdit = () => {
    setEditingTriageField(null);
    setEditingTriageData({});
  };

  // Helper to get current category (selected or suggested)
  const getCurrentCategory = (): number | null => {
    return triageCategoryFinal ?? currentReferral?.triage_data?.triage_category_suggested ?? null;
  };

  // Helper to get category-specific styles
  const getCategoryStyles = (category: number | null): React.CSSProperties => {
    const styles: Record<number, React.CSSProperties> = {
      1: {
        backgroundColor: 'rgba(255, 214, 31, 0.1)',
        borderColor: 'var(--axis-accent-yellow, #ffa600)',
        color: 'var(--axis-accent-yellow, #ffa600)'
      },
      2: {
        backgroundColor: 'rgba(232, 56, 162, 0.1)',
        borderColor: 'var(--axis-accent-pink, #E838A2)',
        color: 'var(--axis-accent-pink, #E838A2)'
      },
      3: {
        backgroundColor: 'rgba(56, 220, 232, 0.1)',
        borderColor: 'var(--axis-accent-blue, #38DCE8)',
        color: 'var(--axis-accent-blue, #38DCE8)'
      },
      4: {
        backgroundColor: 'rgba(56, 232, 146, 0.1)',
        borderColor: 'var(--axis-accent-green, #38e892)',
        color: 'var(--axis-accent-green, #38e892)'
      }
    };
    return category && category in styles ? styles[category] : {};
  };

  // Check if any of the editable fields (Theme, Provisional Diagnosis, Triage Category, Complexity, Clinical Fields, Referral Type) have been changed
  const hasEditableChanges = () => {
    if (!currentReferral?.triage_data) return false;

    // Check Theme
    const themeChanged = editingTriageData.theme !== undefined &&
      editingTriageData.theme !== (currentReferral.triage_data.theme || null);

    // Check Provisional Diagnosis
    const provisionalDiagnosisChanged =
      editingTriageData.provisional_diagnosis !== undefined &&
      editingTriageData.provisional_diagnosis !== (currentReferral.triage_data.provisional_diagnosis || null);

    // Check Triage Category
    const originalCategory = currentReferral.triage_category_final || null;
    const currentCategory = getCurrentCategory();
    const triageCategoryChanged = currentCategory !== null && currentCategory !== originalCategory;

    // Check Complexity
    const originalComplexity = currentReferral.triage_data.complexity || null;
    const complexityChanged = complexityValue !== originalComplexity;

    // AXIS165: Check editable clinical fields
    const originalKeyPhrases = currentReferral.triage_data.key_phrases_matched || [];
    const keyPhrasesChanged = JSON.stringify(editableTriageFields.key_phrases_matched) !== JSON.stringify(originalKeyPhrases);

    const originalSymptoms = currentReferral.triage_data.primary_symptoms || [];
    const symptomsChanged = JSON.stringify(editableTriageFields.primary_symptoms) !== JSON.stringify(originalSymptoms);

    const originalLimitations = currentReferral.triage_data.functional_limitations || [];
    const limitationsChanged = JSON.stringify(editableTriageFields.functional_limitations) !== JSON.stringify(originalLimitations);

    const originalEscalation = currentReferral.triage_data.escalation_required ?? null;
    const escalationChanged = editableTriageFields.escalation_required !== originalEscalation;

    const originalEscalationReason = currentReferral.triage_data.escalation_reason || null;
    const escalationReasonChanged = editableTriageFields.escalation_reason !== originalEscalationReason;

    // AXIS165: Check Referral Type change
    const originalReferralType = currentReferral.referral_type || null;
    const referralTypeChanged = referralTypeValue !== originalReferralType;

    const clinicalNotesChanged =
      srReferral && clinicalNotesValue !== (currentReferral.clinical_notes || '');

    return themeChanged || provisionalDiagnosisChanged || triageCategoryChanged || complexityChanged ||
           keyPhrasesChanged || symptomsChanged || limitationsChanged || escalationChanged || escalationReasonChanged ||
           referralTypeChanged || clinicalNotesChanged;
  };

  // Save all editable fields at once
  const handleSaveAllEditableFields = async () => {
    if (!currentReferral) return;

    try {
      setSavingTriage(true);
      setTriageError(null);

      const triageDataUpdates: Partial<TriageData> = {};

      // Only add fields that have been edited
      if (editingTriageData.theme !== undefined) {
        triageDataUpdates.theme = editingTriageData.theme;
      }
      if (editingTriageData.provisional_diagnosis !== undefined) {
        triageDataUpdates.provisional_diagnosis = editingTriageData.provisional_diagnosis;
      }

      // Check if complexity has changed
      const originalComplexity = currentReferral.triage_data?.complexity || null;
      if (complexityValue !== originalComplexity) {
        triageDataUpdates.complexity = complexityValue;
      }

      // AXIS165: Check and add editable clinical fields
      const originalKeyPhrases = currentReferral.triage_data?.key_phrases_matched || [];
      if (JSON.stringify(editableTriageFields.key_phrases_matched) !== JSON.stringify(originalKeyPhrases)) {
        triageDataUpdates.key_phrases_matched = editableTriageFields.key_phrases_matched;
      }

      const originalSymptoms = currentReferral.triage_data?.primary_symptoms || [];
      if (JSON.stringify(editableTriageFields.primary_symptoms) !== JSON.stringify(originalSymptoms)) {
        triageDataUpdates.primary_symptoms = editableTriageFields.primary_symptoms;
      }

      const originalLimitations = currentReferral.triage_data?.functional_limitations || [];
      if (JSON.stringify(editableTriageFields.functional_limitations) !== JSON.stringify(originalLimitations)) {
        triageDataUpdates.functional_limitations = editableTriageFields.functional_limitations;
      }

      const originalEscalation = currentReferral.triage_data?.escalation_required ?? null;
      if (editableTriageFields.escalation_required !== originalEscalation) {
        triageDataUpdates.escalation_required = editableTriageFields.escalation_required;
      }

      const originalEscalationReason = currentReferral.triage_data?.escalation_reason || null;
      if (editableTriageFields.escalation_reason !== originalEscalationReason) {
        triageDataUpdates.escalation_reason = editableTriageFields.escalation_reason;
      }

      // Determine triage category to save
      const currentCategory = getCurrentCategory();
      const categoryToSave = currentCategory !== null && currentCategory !== currentReferral.triage_category_final
        ? currentCategory
        : undefined;

      // Save triage category and all triage data fields (including clinical fields)
      let updatedReferral = currentReferral;
      if (categoryToSave !== undefined || Object.keys(triageDataUpdates).length > 0) {
        updatedReferral = await ApiService.updateTriageCategory(
          currentReferral.id,
          categoryToSave,
          Object.keys(triageDataUpdates).length > 0 ? triageDataUpdates : undefined
        );
      }

      // AXIS165: Save referral type if it changed (separate API call since it's on referrals table)
      const originalReferralType = currentReferral.referral_type || null;
      if (referralTypeValue !== originalReferralType) {
        updatedReferral = await ApiService.updateReferral(currentReferral.id, {
          referral_type: referralTypeValue
        });
      }

      if (srReferral && clinicalNotesValue !== (currentReferral.clinical_notes || '')) {
        updatedReferral = await ApiService.updateReferral(currentReferral.id, {
          clinical_notes: clinicalNotesValue || null,
        });
      }

      // Preserve existing attachments
      const mergedReferral = {
        ...currentReferral,
        ...updatedReferral,
        attachments: (updatedReferral.attachments && updatedReferral.attachments.length > 0)
          ? updatedReferral.attachments
          : currentReferral.attachments || []
      };

      setCurrentReferral(mergedReferral);
      setEditingTriageField(null);
      setEditingTriageData({});
      setTriageCategoryFinal(mergedReferral.triage_category_final || null);
      // Update complexity value from the saved referral
      setComplexityValue(mergedReferral.triage_data?.complexity || null);
      // AXIS165: Update referral type value from the saved referral
      setReferralTypeValue(mergedReferral.referral_type || null);
      setClinicalNotesValue(mergedReferral.clinical_notes || '');

      // AXIS165: Update editable fields and overridden fields from the saved referral
      if (mergedReferral.triage_data) {
        setEditableTriageFields({
          key_phrases_matched: mergedReferral.triage_data.key_phrases_matched || [],
          primary_symptoms: mergedReferral.triage_data.primary_symptoms || [],
          functional_limitations: mergedReferral.triage_data.functional_limitations || [],
          escalation_required: mergedReferral.triage_data.escalation_required ?? null,
          escalation_reason: mergedReferral.triage_data.escalation_reason || null
        });
      }

      if (mergedReferral.overridden_triage_fields) {
        setOverriddenFields(new Set(mergedReferral.overridden_triage_fields));
      }

      onReferralUpdate?.(mergedReferral);
    } catch (err) {
      setTriageError(err instanceof Error ? err.message : 'Failed to save triage fields');
    } finally {
      setSavingTriage(false);
    }
  };

  // Wrapper functions for EditableField component
  const createEditableFieldHandlers = (fieldName: string, convertValue?: (value: string) => any) => {
    return {
      onStartEdit: () => handleStartEditingTriageField(fieldName),
      onChange: (value: string) => {
        const convertedValue = convertValue ? convertValue(value) : value;
        handleTriageFieldChange(fieldName, convertedValue);
      },
      onBlur: () => {
        // Don't auto-save, just keep the edit state for the save button
        // The save button will handle saving all changes
      }
    };
  };


  const handleApproveTriage = async () => {
    if (!currentReferral) return;
    
    try {
      setApprovingTriage(true);
      setTriageError(null);
      
      // Determine which category to use for final
      let categoryToSave: number | null = null;
      
      // Priority: 1. Selected in dropdown, 2. Already saved final, 3. Suggested category
      if (triageCategoryFinal !== null) {
        categoryToSave = triageCategoryFinal;
      } else if (currentReferral.triage_category_final !== null && currentReferral.triage_category_final !== undefined) {
        categoryToSave = currentReferral.triage_category_final;
      } else if (currentReferral.triage_data?.triage_category_suggested !== null && 
                 currentReferral.triage_data?.triage_category_suggested !== undefined) {
        categoryToSave = currentReferral.triage_data.triage_category_suggested;
      }
      
      // If we have a category but it's not saved as final yet, save it first
      if (categoryToSave !== null && categoryToSave !== currentReferral.triage_category_final) {
        await ApiService.updateTriageCategory(currentReferral.id, categoryToSave);
      }
      
      // Approve the triage
      await ApiService.approveTriage(currentReferral.id);
      
      // Fetch the full updated referral to get all fields
      const fullReferral = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(fullReferral);

      if (onReferralUpdate) {
        onReferralUpdate(fullReferral);
      }

      // AXIS206: Close modal automatically on successful approval
      onClose();
    } catch (err) {
      setTriageError(err instanceof Error ? err.message : 'Failed to approve triage');
    } finally {
      setApprovingTriage(false);
    }
  };

  // AXIS209: Handle approve for triage (admin returns referral to triaged tab)
  // Uses the same endpoint as AXIS210 (promote miscellaneous to triage)
  const handleApproveForTriage = async () => {
    if (!currentReferral) return;
    
    try {
      setApprovingForTriage(true);
      setTriageError(null);
      
      await ApiService.approveForTriage(currentReferral.id);
      
      // Fetch the full updated referral
      const fullReferral = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(fullReferral);

      if (onReferralUpdate) {
        onReferralUpdate(fullReferral);
      }

      // Close modal automatically on successful approval for triage
      onClose();
    } catch (err) {
      setTriageError(err instanceof Error ? err.message : 'Failed to approve for triage');
    } finally {
      setApprovingForTriage(false);
    }
  };

  // AXIS209: Handle assign to admin from modal actions dropdown
  const handleAssignToAdmin = async () => {
    if (!currentReferral) return;
    
    try {
      setAssigningToAdmin(true);
      setTriageError(null);
      
      await ApiService.assignToAdmin(currentReferral.id);
      
      // Fetch the full updated referral
      const fullReferral = await ApiService.getReferralById(currentReferral.id);
      setCurrentReferral(fullReferral);

      if (onReferralUpdate) {
        onReferralUpdate(fullReferral);
      }

      // Close modal automatically after assigning to admin
      onClose();
    } catch (err) {
      setTriageError(err instanceof Error ? err.message : 'Failed to assign to admin');
    } finally {
      setAssigningToAdmin(false);
    }
  };

  return (
    <>
      {/* Detail Modal */}
      <div className="modal-overlay" onClick={onClose}>
        <div 
          className="modal-content" 
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: 'calc(100vh - 10rem)', display: 'flex', flexDirection: 'column' }}
        >
          <div className="modal-header">
            <h2>
              {currentReferral.patient_first_name} {currentReferral.patient_last_name}
            </h2>
            <div className="modal-actions">
              <Button
                variant="success"
                onClick={handleSaveAllEditableFields}
                disabled={!hasEditableChanges() || savingTriage}
                isLoading={savingTriage}
                style={{ marginRight: '0.5rem' }}
                size="sm"
              >
                Save Changes
              </Button>
              {!currentReferral.triage_approved && !currentReferral.assigned_to_admin && (
                <Button
                  variant="success"
                  onClick={handleApproveTriage}
                  disabled={approvingTriage || (!currentReferral.triage_category_final && !triageCategoryFinal && !currentReferral.triage_data?.triage_category_suggested)}
                  isLoading={approvingTriage}
                  style={{ marginRight: '0.5rem' }}
                  size="sm"
                >
                  Approve Triage
                </Button>
              )}
              {/* AXIS209: Approve For Triage button - shown when assigned_to_admin is TRUE */}
              {!currentReferral.triage_approved && currentReferral.assigned_to_admin && (
                <Button
                  variant="success"
                  onClick={handleApproveForTriage}
                  disabled={approvingForTriage}
                  isLoading={approvingForTriage}
                  style={{ marginRight: '0.5rem' }}
                  size="sm"
                >
                  Approve For Triage
                </Button>
              )}
              {/* AXIS209: Actions dropdown - always shown when not triage_approved */}
              {userRole && !currentReferral.triage_approved && (
                <div style={{ marginRight: '0.5rem', display: 'inline-block' }}>
                  <ActionMenu
                    icon="more-horizontal"
                    iconSize={20}
                    isLoading={reprocessing || assigningToAdmin || approvingForTriage}
                    options={[
                      {
                        label: showReprocessAfterUpload ? 'Reprocess (recommended)' : 'Reprocess',
                        onClick: handleReprocessTriage,
                        isLoading: reprocessing,
                        disabled: reprocessing || assigningToAdmin || approvingForTriage || !canReprocessTriage,
                        variant: showReprocessAfterUpload ? 'warning' : 'default'
                      },
                      // AXIS209: Show "Assign to Admin" option only when not assigned to admin
                      ...(userRole && ['admin', 'manager', 'clinician'].includes(userRole) && !currentReferral.assigned_to_admin ? [
                        {
                          label: 'Assign to Admin',
                          onClick: handleAssignToAdmin,
                          isLoading: assigningToAdmin,
                          disabled: assigningToAdmin || reprocessing || approvingForTriage,
                          variant: 'warning' as const
                        }
                      ] : [])
                    ]}
                  />
                </div>
              )}
              {/* Show standalone reprocess button when triage is approved (no dropdown) */}
              {userRole && currentReferral.triage_approved && (
                <Button
                  variant={showReprocessAfterUpload ? "warning" : "secondary"}
                  onClick={handleReprocessTriage}
                  disabled={reprocessing}
                  isLoading={reprocessing}
                  style={{ marginRight: '0.5rem' }}
                  size="sm"
                  title={showReprocessAfterUpload ? "New attachment uploaded - reprocess recommended" : "Reprocess Triage"}
                >
                  {!reprocessing && <FeatherIcon icon="refresh-cw" size={14} style={{ marginRight: '4px' }} />}
                  Reprocess
                </Button>
              )}
              {currentReferral.triage_approved && (
                <span className="triage-approved-badge" style={{ marginRight: '0.5rem' }}>
                  ✓ Approved
                  {currentReferral.triage_approved_at && (
                    <span style={{ marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                      on {formatDateAsDDMMYYYY(currentReferral.triage_approved_at)}
                    </span>
                  )}
                </span>
              )}
              <Button
                variant="icon"
                onClick={onClose}
                title="Close"
              >
                <FeatherIcon icon="x" size={20} />
              </Button>
            </div>
          </div>

          <div className="modal-body">
            {isSrReferral(currentReferral) && (
              <SrChangeBanner
                referral={currentReferral}
                onViewAddendum={(attachmentId) => {
                  setFocusAttachmentId(attachmentId);
                  setActiveTab('overview');
                }}
              />
            )}
            <TabMenu variant="queue">
              <TabMenuItem
                id="overview"
                label="Overview"
                isActive={activeTab === 'overview'}
                onClick={() => setActiveTab('overview')}
              />
              <TabMenuItem
                id="attachments"
                label="Attachments"
                isActive={activeTab === 'attachments'}
                onClick={() => setActiveTab('attachments')}
              />
            </TabMenu>

            <div className="tab-content">
              {activeTab === 'overview' && (
                <div className="overview-tab">
                  <div className="overview-content">
                    {isManualTriageShell && (
                      <div
                        style={{
                          marginBottom: '1rem',
                          padding: '10px 12px',
                          backgroundColor: '#fffbeb',
                          border: '1px solid #fcd34d',
                          borderRadius: '6px',
                          fontSize: '0.875rem',
                          color: '#92400e',
                        }}
                      >
                        Manual triage — AI extraction did not run. Enter clinical notes and triage
                        fields below, then save and approve.
                      </div>
                    )}
                    {currentReferral.triage_data ? (
                      <div className="overview-data">
                        {/* 1. Triage Information Section */}
                        <div className="patient-details" data-testid="triage-information-section">
                          <h4>Triage Information</h4>
                          <div className="detail-grid" style={{ gridTemplateColumns: '1fr' }}>
                            {/* Theme */}
                            <EditableField
                              label="Theme"
                              value={editingTriageData.theme !== undefined ? editingTriageData.theme : currentReferral.triage_data.theme}
                              fieldName="theme"
                              isEditing={editingTriageField === 'theme'}
                              onStartEdit={createEditableFieldHandlers('theme').onStartEdit}
                              onChange={createEditableFieldHandlers('theme').onChange}
                              onBlur={createEditableFieldHandlers('theme').onBlur}
                              type="text"
                            />

                            {/* Provisional Diagnosis */}
                            <div className="detail-item">
                              <label>Provisional Diagnosis:</label>
                              {editingTriageField === 'provisional_diagnosis' ? (
                                <div>
                                  <textarea
                                    value={(editingTriageData.provisional_diagnosis !== undefined ? editingTriageData.provisional_diagnosis : (currentReferral.triage_data?.provisional_diagnosis || '')) || ''}
                                    onChange={(e) => handleTriageFieldChange('provisional_diagnosis', e.target.value)}
                                    className="form-control"
                                    rows={3}
                                    autoFocus
                                    onKeyDown={(e) => {
                                      if (e.key === 'Escape') {
                                        handleCancelTriageFieldEdit();
                                      }
                                    }}
                                  />
                                </div>
                              ) : (
                                <span 
                                  className="editable-field"
                                  onClick={() => handleStartEditingTriageField('provisional_diagnosis')}
                                >
                                  {currentReferral.triage_data.provisional_diagnosis || 'N/A'}
                                </span>
                              )}
                            </div>

                            {/* Symptom Duration */}
                            {currentReferral.date_of_injury && (
                              <div className="detail-item">
                                <label>Symptom Duration:</label>
                                <span>
                                  {(() => {
                                    const injuryDate = new Date(currentReferral.date_of_injury);
                                    const today = new Date();
                                    const diffTime = Math.abs(today.getTime() - injuryDate.getTime());
                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                    if (diffDays < 7) {
                                      return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
                                    } else if (diffDays < 30) {
                                      const weeks = Math.floor(diffDays / 7);
                                      return `${weeks} week${weeks !== 1 ? 's' : ''}`;
                                    } else if (diffDays < 365) {
                                      const months = Math.floor(diffDays / 30);
                                      return `${months} month${months !== 1 ? 's' : ''}`;
                                    } else {
                                      const years = Math.floor(diffDays / 365);
                                      return `${years} year${years !== 1 ? 's' : ''}`;
                                    }
                                  })()}
                                </span>
                              </div>
                            )}

                            {/* AXISREF438: Priority and Complexity moved to the dedicated
                                Complexity section below (grouped with Referral Type). */}

                            {/* AXIS208: Date of Birth - positioned above Key Phrases */}
                            <div className="detail-item">
                              <label>Date of Birth:</label>
                              <span>{formatDOB(currentReferral.patient_dob)}</span>
                            </div>

                            {/* Key Phrases - AXIS165: Editable */}
                            <div className="detail-item full-width">
                              <label>
                                Key Phrases:
                                {renderEditedBadge('key_phrases_matched')}
                              </label>
                              <TagInput
                                tags={editableTriageFields.key_phrases_matched}
                                onTagsChange={(tags: string[]) => setEditableTriageFields(prev => ({ ...prev, key_phrases_matched: tags }))}
                                placeholder="Type and press Enter to add key phrases..."
                                disabled={currentReferral.triage_approved}
                              />
                            </div>

                            {/* Red Flags */}
                            {currentReferral.triage_data.red_flags_detected && Object.keys(currentReferral.triage_data.red_flags_detected).length > 0 && (
                              <div className="detail-item full-width">
                                <label>Red Flags:</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.25rem' }}>
                                  {Object.entries(currentReferral.triage_data.red_flags_detected)
                                    .filter(([_, value]) => value === true)
                                    .map(([flag, _]) => (
                                      <span key={flag} className="badge badge-danger">
                                        {toCapitalCase(flag.replace(/_/g, ' '))}
                                      </span>
                                    ))}
                                  {Object.entries(currentReferral.triage_data.red_flags_detected).filter(([_, value]) => value === true).length === 0 && (
                                    <span style={{ color: '#666' }}>None</span>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* Escalation Required - AXIS165: Editable toggle */}
                            <div className="detail-item full-width">
                              <label>
                                Escalation Required:
                                {renderEditedBadge('escalation_required')}
                              </label>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.25rem' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: currentReferral.triage_approved ? 'not-allowed' : 'pointer' }}>
                                  <input
                                    type="radio"
                                    name="escalation_required"
                                    checked={editableTriageFields.escalation_required === true}
                                    onChange={() => setEditableTriageFields(prev => ({ ...prev, escalation_required: true }))}
                                    disabled={currentReferral.triage_approved}
                                  />
                                  <span>Yes</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: currentReferral.triage_approved ? 'not-allowed' : 'pointer' }}>
                                  <input
                                    type="radio"
                                    name="escalation_required"
                                    checked={editableTriageFields.escalation_required === false}
                                    onChange={() => setEditableTriageFields(prev => ({ ...prev, escalation_required: false, escalation_reason: null }))}
                                    disabled={currentReferral.triage_approved}
                                  />
                                  <span>No</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: currentReferral.triage_approved ? 'not-allowed' : 'pointer' }}>
                                  <input
                                    type="radio"
                                    name="escalation_required"
                                    checked={editableTriageFields.escalation_required === null}
                                    onChange={() => setEditableTriageFields(prev => ({ ...prev, escalation_required: null, escalation_reason: null }))}
                                    disabled={currentReferral.triage_approved}
                                  />
                                  <span>N/A</span>
                                </label>
                              </div>
                            </div>

                            {/* Escalation Reason - AXIS165: Editable text field, shown when escalation_required is true */}
                            {editableTriageFields.escalation_required === true && (
                              <div className="detail-item full-width">
                                <label>
                                  Escalation Explanation:
                                  {renderEditedBadge('escalation_reason')}
                                </label>
                                <textarea
                                  value={editableTriageFields.escalation_reason || ''}
                                  onChange={(e) => setEditableTriageFields(prev => ({ ...prev, escalation_reason: e.target.value }))}
                                  placeholder="Enter escalation reason..."
                                  rows={2}
                                  className="form-control"
                                  disabled={currentReferral.triage_approved}
                                  style={{ marginTop: '0.25rem' }}
                                />
                              </div>
                            )}

                            {/* Onset Type */}
                            {currentReferral.triage_data.onset_type && (
                              <div className="detail-item">
                                <label>Onset Type:</label>
                                <span>{toCapitalCase(currentReferral.triage_data.onset_type)}</span>
                              </div>
                            )}

                            {/* Athlete Status */}
                            <div className="detail-item">
                              <label>Athlete Status:</label>
                              <span>
                                {currentReferral.triage_data.athlete_status !== null ? (currentReferral.triage_data.athlete_status ? 'Yes' : 'No') : 'N/A'}
                              </span>
                            </div>

                            {/* Athlete Level */}
                            {currentReferral.triage_data.athlete_status && currentReferral.triage_data.athlete_level && (
                              <div className="detail-item">
                                <label>Athlete Level:</label>
                                <span>{toCapitalCase(currentReferral.triage_data.athlete_level)}</span>
                              </div>
                            )}

                            {/* Sport Type */}
                            {currentReferral.triage_data.athlete_status && currentReferral.triage_data.sport_type && (
                              <div className="detail-item">
                                <label>Sport Type:</label>
                                <span>{toCapitalCase(currentReferral.triage_data.sport_type)}</span>
                              </div>
                            )}

                            {/* Post Surgical */}
                            <div className="detail-item">
                              <label>Post Surgical:</label>
                              <span>
                                {currentReferral.triage_data.post_surgical_status !== null ? (currentReferral.triage_data.post_surgical_status ? 'Yes' : 'No') : 'N/A'}
                              </span>
                            </div>

                            {/* Primary Symptoms - AXIS165: Editable */}
                            <div className="detail-item full-width">
                              <label>
                                Primary Symptoms:
                                {renderEditedBadge('primary_symptoms')}
                              </label>
                              <TagInput
                                tags={editableTriageFields.primary_symptoms}
                                onTagsChange={(tags: string[]) => setEditableTriageFields(prev => ({ ...prev, primary_symptoms: tags }))}
                                placeholder="Type and press Enter to add symptoms..."
                                disabled={currentReferral.triage_approved}
                              />
                            </div>

                            {/* Functional Limitations - AXIS165: Editable */}
                            <div className="detail-item full-width">
                              <label>
                                Functional Limitations:
                                {renderEditedBadge('functional_limitations')}
                              </label>
                              <TagInput
                                tags={editableTriageFields.functional_limitations}
                                onTagsChange={(tags: string[]) => setEditableTriageFields(prev => ({ ...prev, functional_limitations: tags }))}
                                placeholder="Type and press Enter to add limitations..."
                                disabled={currentReferral.triage_approved}
                              />
                            </div>

                            {/* Comorbidities */}
                            {currentReferral.triage_data.comorbidities && currentReferral.triage_data.comorbidities.length > 0 && (
                              <div className="detail-item full-width">
                                <label>Comorbidities:</label>
                                <ul style={{ margin: '0.25rem 0 0 0', paddingLeft: '1.5rem' }}>
                                  {currentReferral.triage_data.comorbidities.map((comorbidity, idx) => (
                                    <li key={idx}>{comorbidity}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {triageError && (
                            <div className="triage-error" style={{ color: 'red', marginTop: '0.5rem' }}>
                              {triageError}
                            </div>
                          )}
                        </div>

                        {/* AXISREF438: Dedicated Complexity section — Priority, Complexity,
                            and Referral Type grouped together in this order. */}
                        <div className="patient-details" data-testid="complexity-section">
                          <h4>Complexity</h4>
                          <div className="detail-grid" style={{ gridTemplateColumns: '1fr' }}>
                            {/* Priority */}
                            <div className="detail-item" data-testid="complexity-priority">
                              <label>
                                Priority:
                              </label>
                              <select
                                value={getCurrentCategory()?.toString() || ''}
                                onChange={(e) => setTriageCategoryFinal(e.target.value ? parseInt(e.target.value) : null)}
                                className="form-control"
                                disabled={currentReferral.triage_approved}
                                style={getCategoryStyles(getCurrentCategory())}
                              >
                                <option value="">Select priority</option>
                                <option value="1">P1 - Urgent</option>
                                <option value="2">P2 - Priority</option>
                                <option value="3">P3 - Routine</option>
                                <option value="4">P4 - Non-urgent</option>
                              </select>
                            </div>

                            {/* Complexity */}
                            <div className="detail-item" data-testid="complexity-complexity">
                              <label>Complexity:</label>
                              <select
                                value={complexityValue || ''}
                                onChange={(e) => setComplexityValue(e.target.value as 'acute' | 'simple' | 'complex' | null || null)}
                                className="form-control"
                                disabled={currentReferral.triage_approved}
                              >
                                <option value="">Select complexity</option>
                                <option value="acute">Acute</option>
                                <option value="simple">Simple</option>
                                <option value="complex">Complex</option>
                              </select>
                            </div>

                            {/* Referral Type — moved from Referral Information (AXISREF438).
                                State, options, stale badge, validation and persistence unchanged. */}
                            <div className="detail-item" data-testid="complexity-referral-type">
                              <label>
                                Referral Type:
                                {/* Warning indicator for stale values */}
                                {referralTypeValue && ['osteoarthritis_clinic', 'reds'].includes(referralTypeValue) && (
                                  <span
                                    style={{
                                      fontSize: '0.7rem',
                                      padding: '2px 6px',
                                      backgroundColor: '#ff9800',
                                      color: 'white',
                                      borderRadius: '4px',
                                      marginLeft: '0.5rem',
                                      fontWeight: 500
                                    }}
                                    title="Value not in current list — please update"
                                  >
                                    Stale Value
                                  </span>
                                )}
                              </label>
                              <select
                                value={referralTypeValue || ''}
                                onChange={(e) => setReferralTypeValue(e.target.value || null)}
                                className="form-control"
                                disabled={currentReferral.triage_approved}
                              >
                                <option value="">Select type</option>
                                <option value="acute_knee_clinic">Acute Knee Clinic</option>
                                <option value="acute_shoulder_clinic">Acute Shoulder Clinic</option>
                                <option value="acute_sports_concussion_clinic">Acute Sports Concussion Clinic</option>
                                <option value="concussion_clinic">Concussion Clinic</option>
                                <option value="nutrition">Nutrition</option>
                                <option value="orthopaedic_surgery">Orthopaedic Surgery</option>
                                <option value="performance_health">Performance Health</option>
                                <option value="physiotherapy">Physiotherapy</option>
                                <option value="physiotherapy_pelvic">Physiotherapy - Pelvic</option>
                                <option value="physiotherapy_specialist">Physiotherapy - Specialist</option>
                                <option value="physiotherapy_vestibular">Physiotherapy – Vestibular</option>
                                <option value="podiatry">Podiatry</option>
                                <option value="procedures">Procedures</option>
                                <option value="shoulder_pain_clinic">Shoulder Pain Clinic</option>
                                <option value="sports_exercise_medicine">Sport and Exercise Medicine</option>
                                <option value="vocational_rehabilitation">Vocational Rehabilitation</option>
                              </select>
                            </div>
                          </div>
                        </div>

                        {/* 2. Referral Information Section */}
                        <div className="patient-details">
                          <h4>Referral Information</h4>
                          <div className="detail-grid">
                            {/* Age (calculated from DOB) */}
                            {currentReferral.patient_dob && (
                              <div className="detail-item">
                                <label>Age:</label>
                                <span>
                                  {(() => {
                                    const dob = new Date(currentReferral.patient_dob);
                                    const today = new Date();
                                    let age = today.getFullYear() - dob.getFullYear();
                                    const monthDiff = today.getMonth() - dob.getMonth();
                                    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
                                      age--;
                                    }
                                    return age;
                                  })()} years
                                </span>
                              </div>
                            )}

                            {/* Basic referral fields */}
                            <div className="detail-item">
                              <label>NHI Number:</label>
                              <span>{currentReferral.nhi_number || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Date of Birth:</label>
                              <span>{formatDateAsDDMMYYYY(currentReferral.patient_dob)}</span>
                            </div>
                            <div className="detail-item">
                              <label>Contact Phone:</label>
                              <span>{currentReferral.contact_phone || 'N/A'}</span>
                            </div>
                            <div className="detail-item">
                              <label>Contact Email:</label>
                              <span>{currentReferral.contact_email || 'N/A'}</span>
                            </div>
                            {srReferral && isClinicalRole ? (
                              <div className="detail-item full-width">
                                <label>Clinical Notes:</label>
                                <textarea
                                  className="form-control"
                                  rows={4}
                                  value={clinicalNotesValue}
                                  onChange={(e) => setClinicalNotesValue(e.target.value)}
                                  placeholder="Enter clinical notes from the referral letter"
                                  maxLength={8000}
                                  style={{ marginTop: '0.25rem', width: '100%' }}
                                />
                              </div>
                            ) : currentReferral.clinical_notes ? (
                              <div className="detail-item full-width">
                                <label>Clinical Notes:</label>
                                <span style={{ whiteSpace: 'pre-wrap' }}>{currentReferral.clinical_notes}</span>
                              </div>
                            ) : null}
                          </div>
                        </div>

                        {/* AXIS166: Additional Comments Section */}
                        <div className="patient-details">
                          <h4>Additional Comments</h4>
                          <div className="detail-grid" style={{ gridTemplateColumns: '1fr' }}>
                            {/* Comments List */}
                            <div className="detail-item full-width">
                              {loadingComments ? (
                                <p style={{ color: '#666', fontStyle: 'italic' }}>Loading comments...</p>
                              ) : comments.length === 0 ? (
                                <p style={{ color: '#666', fontStyle: 'italic' }}>No comments yet</p>
                              ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                  {comments.map((comment) => (
                                    <div
                                      key={comment.id}
                                      style={{
                                        padding: '0.75rem',
                                        backgroundColor: '#f8f9fa',
                                        borderRadius: '4px',
                                        border: '1px solid #e9ecef'
                                      }}
                                    >
                                      <div style={{ marginBottom: '0.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontWeight: 500, color: '#333' }}>
                                          {comment.author_name}
                                        </span>
                                        {/* AXIS207: Edit/Delete buttons for clinical staff */}
                                        {isClinicalRole && editingCommentId !== comment.id && (
                                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={() => startEditComment(comment)}
                                              disabled={deletingCommentId === comment.id}
                                            >
                                              <FeatherIcon icon="edit-2" size={14} />
                                            </Button>
                                            <Button
                                              variant="danger"
                                              size="sm"
                                              onClick={() => handleDeleteComment(comment.id)}
                                              disabled={deletingCommentId === comment.id}
                                              isLoading={deletingCommentId === comment.id}
                                            >
                                              <FeatherIcon icon="trash-2" size={14} />
                                            </Button>
                                          </div>
                                        )}
                                      </div>
                                      
                                      {/* AXIS207: Edit mode */}
                                      {editingCommentId === comment.id ? (
                                        <div>
                                          <textarea
                                            value={editCommentBody}
                                            onChange={(e) => setEditCommentBody(e.target.value)}
                                            rows={3}
                                            className="form-control"
                                            maxLength={2000}
                                            style={{ marginBottom: '0.5rem' }}
                                            autoFocus
                                          />
                                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                                            <Button
                                              variant="primary"
                                              size="sm"
                                              onClick={() => handleEditComment(comment.id)}
                                              disabled={submittingComment || !editCommentBody.trim()}
                                              isLoading={submittingComment}
                                            >
                                              Save
                                            </Button>
                                            <Button
                                              variant="secondary"
                                              size="sm"
                                              onClick={cancelEditComment}
                                              disabled={submittingComment}
                                            >
                                              Cancel
                                            </Button>
                                          </div>
                                        </div>
                                      ) : (
                                        <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: '#333' }}>
                                          {comment.body}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/* Add Comment Form */}
                            <div className="detail-item full-width">
                              <label>Add Comment:</label>
                              <textarea
                                value={newCommentBody}
                                onChange={(e) => setNewCommentBody(e.target.value)}
                                placeholder={(currentReferral.triage_approved && !isClinicalRole) ? "Comments are locked after triage approval" : "Enter your comment here..."}
                                rows={3}
                                className="form-control"
                                disabled={(currentReferral.triage_approved && !isClinicalRole) || submittingComment}
                                maxLength={2000}
                                style={{ marginTop: '0.25rem', marginBottom: '0.5rem' }}
                              />
                              {/* Character counter - shown when approaching limit */}
                              {newCommentBody.length > 1800 && (
                                <div style={{
                                  fontSize: '0.8rem',
                                  color: newCommentBody.length >= 2000 ? '#dc3545' : '#ff9800',
                                  marginBottom: '0.5rem'
                                }}>
                                  {newCommentBody.length}/2000 characters
                                  {newCommentBody.length >= 2000 && ' (maximum reached)'}
                                </div>
                              )}
                              <Button
                                variant="primary"
                                onClick={handleSubmitComment}
                                disabled={(currentReferral.triage_approved && !isClinicalRole) || submittingComment || !newCommentBody.trim()}
                                isLoading={submittingComment}
                                size="sm"
                              >
                                Add Comment
                              </Button>
                              {commentsError && (
                                <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                  {commentsError}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="overview-data">
                        <div className="triage-information-section">
                          <p>No triage data available for this referral.</p>
                        </div>
                      </div>
                    )}

                  {/* AXISREF390: Location & Clinic Routing section */}
                  {(currentReferral.patient_suburb || currentReferral.practice_suburb || currentReferral.recommended_clinic_name || currentReferral.recommended_clinic_id) && (() => {
                    const confidence = currentReferral.clinic_recommendation_confidence;
                    const isPracticeFallback = currentReferral.patient_location_source === 'practice_address';
                    const hasClinic = !!(currentReferral.recommended_clinic_name || currentReferral.recommended_clinic_id);
                    return (
                      <div style={{ marginBottom: '1.5rem', padding: '1rem', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                        <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#666' }}>
                          Location &amp; Clinic Routing
                        </h4>

                        {isPracticeFallback && (
                          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef3c7', borderRadius: '4px', fontSize: '0.85rem', color: '#92400e' }}>
                            Patient address not found — location based on referring practice address.
                          </div>
                        )}
                        {confidence != null && confidence < 40 && (
                          <div style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fee2e2', borderRadius: '4px', fontSize: '0.85rem', color: '#991b1b' }}>
                            Low confidence — manual clinic assignment recommended.
                          </div>
                        )}

                        <dl style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '0.6rem', margin: 0 }}>
                          {currentReferral.patient_suburb && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>Patient location</dt>
                              <dd style={{ margin: 0, fontWeight: 500 }}>
                                {currentReferral.patient_suburb}
                                {currentReferral.patient_city ? `, ${currentReferral.patient_city}` : ''}
                              </dd>
                            </div>
                          )}

                          {currentReferral.practice_suburb && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>
                                Referrer location {!isPracticeFallback && <span style={{ opacity: 0.6 }}>(extracted)</span>}
                              </dt>
                              <dd style={{ margin: 0, color: isPracticeFallback ? undefined : '#666' }}>
                                {currentReferral.practice_suburb}
                                {currentReferral.practice_city ? `, ${currentReferral.practice_city}` : ''}
                              </dd>
                            </div>
                          )}

                          {currentReferral.patient_location_source && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>Source used</dt>
                              <dd style={{ margin: 0 }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.78rem',
                                  background: isPracticeFallback ? '#fef3c7' : '#d1fae5',
                                  color: isPracticeFallback ? '#92400e' : '#065f46',
                                }}>
                                  {isPracticeFallback ? 'Practice address' : 'Patient address'}
                                </span>
                              </dd>
                            </div>
                          )}

                          {hasClinic && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>Nearest clinic</dt>
                              <dd style={{ margin: 0, fontWeight: 600 }}>
                                {currentReferral.recommended_clinic_name || `Clinic #${currentReferral.recommended_clinic_id}`}
                              </dd>
                            </div>
                          )}

                          {currentReferral.clinic_recommendation_distance_km != null && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>Distance</dt>
                              <dd style={{ margin: 0 }}>{currentReferral.clinic_recommendation_distance_km.toFixed(1)} km</dd>
                            </div>
                          )}

                          {confidence != null && (
                            <div>
                              <dt style={{ fontSize: '0.78rem', color: '#888', marginBottom: '2px' }}>Confidence</dt>
                              <dd style={{ margin: 0 }}>
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '12px',
                                  fontSize: '0.78rem',
                                  background: confidence >= 70 ? '#d1fae5' : confidence >= 40 ? '#fef3c7' : '#fee2e2',
                                  color: confidence >= 70 ? '#065f46' : confidence >= 40 ? '#92400e' : '#991b1b',
                                }}>
                                  {confidence}%
                                </span>
                              </dd>
                            </div>
                          )}
                        </dl>
                      </div>
                    );
                  })()}

                  </div>

                  {/* PDF Preview Section - Side by side with overview content */}
                  <div className="modal-pdf-preview-section">
                    {currentReferral.attachments && currentReferral.attachments.length > 0 ? (
                      <>
                        <PDFPreviewPanel
                          key={currentReferral.attachments.map(a => a.id).join('-')}
                          attachments={(currentReferral.attachments || []) as any}
                          referralId={currentReferral.id}
                          isLoading={false}
                          userRole={userRole || undefined}
                          isSrReferral={isSrReferral(currentReferral)}
                          focusAttachmentId={focusAttachmentId}
                          onAttachmentUploaded={handleAttachmentUploaded}
                        />
                        {/* AXIS212: Additional upload button below PDF panel when attachments exist - all authenticated users */}
                        {userRole && (
                          <div className="additional-upload" style={{ marginTop: '1rem', textAlign: 'center' }}>
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                              style={{ display: 'none' }}
                              disabled={uploadingAttachment}
                            />
                            <Button
                              variant="secondary"
                              onClick={handleUploadClick}
                              disabled={uploadingAttachment}
                              isLoading={uploadingAttachment}
                              size="sm"
                            >
                              {uploadingAttachment ? (
                                <>
                                  <Spinner size="sm" color="default" />
                                  <span style={{ marginLeft: '0.5rem' }}>{uploadProgress}%</span>
                                </>
                              ) : (
                                <>
                                  <FeatherIcon icon="upload" size={16} style={{ marginRight: '0.5rem' }} />
                                  Upload Another File
                                </>
                              )}
                            </Button>
                            {uploadError && (
                              <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.5rem' }}>
                                {uploadError}
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="no-attachments-notice" style={{ padding: '2rem', textAlign: 'center' }}>
                        <p style={{ marginBottom: '0.5rem', color: '#666' }}>No attachments yet</p>
                        <p className="notice-detail" style={{ fontSize: '0.9rem', color: '#999', marginBottom: '1.5rem' }}>
                          Email body will appear here once received, or upload a file below (PDF, DOCX, DOC)
                        </p>
                        {/* AXIS212: Standalone upload button when no attachments exist - all authenticated users */}
                        {userRole && (
                          <div className="standalone-upload">
                            <input
                              type="file"
                              ref={fileInputRef}
                              onChange={handleFileSelect}
                              accept=".pdf,.docx,.doc,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword"
                              style={{ display: 'none' }}
                              disabled={uploadingAttachment}
                            />
                            <Button
                              variant="secondary"
                              onClick={handleUploadClick}
                              disabled={uploadingAttachment}
                              isLoading={uploadingAttachment}
                              size="sm"
                            >
                              {uploadingAttachment ? (
                                <>
                                  <Spinner size="sm" color="default" />
                                  <span style={{ marginLeft: '0.5rem' }}>{uploadProgress}%</span>
                                </>
                              ) : (
                                <>
                                  <FeatherIcon icon="upload" size={16} style={{ marginRight: '0.5rem' }} />
                                  Upload File
                                </>
                              )}
                            </Button>
                            {uploadError && (
                              <div style={{ color: '#dc3545', fontSize: '0.9rem', marginTop: '0.75rem' }}>
                                {uploadError}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeTab === 'attachments' && (() => {
                // Filter out inline images (email signatures, logos, CID-referenced images)
                // These are internal processing artifacts already embedded in email body PDFs
                const userAttachments = (currentReferral.attachments || []).filter(att => {
                  const filename = att.filename?.toLowerCase() || '';
                  return !filename.startsWith('inline_');
                });
                
                return (
                  <div className="attachments-tab">
                    {userAttachments.length > 0 ? (
                      <div className="attachments-list">
                        {userAttachments.map((attachment) => (
                          <div key={attachment.id} className="attachment-item">
                            <div className="attachment-info">
                              <span className="attachment-name">{attachment.filename}</span>
                              <span className="attachment-size">{formatFileSize(attachment.size)}</span>
                            </div>
                            <div className="attachment-actions">
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={() => handleDownloadAttachment(attachment)}
                                disabled={downloadingAttachments.has(attachment.id)}
                                isLoading={downloadingAttachments.has(attachment.id)}
                              >
                                Download
                              </Button>
                              {/* AXIS241: Remove attachment button - all authenticated users can delete attachments */}
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => handleDeleteClick(attachment)}
                                disabled={deletingAttachments.has(attachment.id)}
                                isLoading={deletingAttachments.has(attachment.id)}
                                title="Remove this attachment"
                              >
                                Remove
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p>No attachments available for this referral.</p>
                    )}

                    <IngestionWarningsBanner
                      warnings={currentReferral.ingestion_warnings || []}
                      dismissed={dismissedIngestionWarnings}
                      onDismiss={() => setDismissedIngestionWarnings(true)}
                    />
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* AXIS212: Reprocess Confirmation Modal */}
      <ConfirmationModal
        isOpen={showReprocessConfirm}
        title="Reprocess Triage"
        message={
          <div>
            {hasOverriddenFields && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#E84B37', fontWeight: 500 }}>
                  ⚠️ Warning: Manual edits will be overwritten
                </p>
                <p>The following fields have been manually edited and will be reset:</p>
                <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                  {Array.from(overriddenFields).map(field => (
                    <li key={field}>{field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</li>
                  ))}
                </ul>
              </div>
            )}
            {isTriageApproved && (
              <div style={{ marginBottom: '1rem' }}>
                <p style={{ color: '#E84B37', fontWeight: 500 }}>
                  ⚠️ Warning: Triage is approved
                </p>
                <p>Reprocessing will reset the approval status. You will need to re-approve the triage.</p>
              </div>
            )}
            <p>Are you sure you want to reprocess the triage for this referral?</p>
          </div>
        }
        confirmLabel="Reprocess"
        cancelLabel="Cancel"
        onConfirm={executeReprocess}
        onCancel={() => setShowReprocessConfirm(false)}
        isLoading={reprocessing}
      />

      {/* AXIS241: Delete Attachment Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Remove Attachment"
        message="Are you sure you want to remove this attachment? This action cannot be undone."
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={attachmentToDelete ? deletingAttachments.has(attachmentToDelete.id) : false}
        variant="danger"
        details={attachmentToDelete ? [
          { label: 'Filename', value: attachmentToDelete.filename },
          { label: 'Size', value: formatFileSize(attachmentToDelete.size) }
        ] : []}
      />
    </>
  );
};
